import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import axios from "axios";
import { toast } from "sonner";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Bell, CheckCircle, Warning, Info, XCircle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 60_000;  // 1 min

const SEV_META = {
  danger: { icon: XCircle, color: "text-red-600", bg: "bg-red-50 border-red-200" },
  warning: { icon: Warning, color: "text-amber-600", bg: "bg-amber-50 border-amber-200" },
  info: { icon: Info, color: "text-blue-600", bg: "bg-blue-50 border-blue-200" },
  success: { icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200" },
};

function fmtRelative(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return i18n.t("notif.justNow");
    if (mins < 60) return i18n.t("notif.minsAgo", { n: mins });
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return i18n.t("notif.hoursAgo", { n: hrs });
    const days = Math.floor(hrs / 24);
    if (days < 30) return i18n.t("notif.daysAgo", { n: days });
    return d.toLocaleDateString(i18n.language === "en" ? "en-US" : "es-MX");
  } catch { return ""; }
}

export default function NotificationBell({ variant = "dark" }) {
  const { t } = useTranslation();
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notifications?limit=30`, { withCredentials: true });
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch {/* silent — auth still loading */}
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-read`, { all: true }, { withCredentials: true });
      await load();
      toast.success(t("notif.markedRead"));
    } catch {
      toast.error(t("notif.markError"));
    }
  };

  const openItem = async (n) => {
    if (!n.read) {
      try {
        await axios.post(`${API}/notifications/mark-read`, { ids: [n.id] }, { withCredentials: true });
        await load();
      } catch {/* ignore */}
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const isDark = variant === "dark";
  const btnClass = isDark
    ? "relative w-9 h-9 flex items-center justify-center rounded-sm text-white hover:bg-zinc-900 transition-colors"
    : "relative w-9 h-9 flex items-center justify-center rounded-sm text-slate-700 hover:bg-slate-100 transition-colors";

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button className={btnClass} aria-label={t("notif.title")} data-testid="notification-bell">
          <Bell size={20} weight={unread > 0 ? "fill" : "regular"} />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--brand,#E31E24)] text-white text-[10px] font-bold flex items-center justify-center"
              data-testid="notification-badge"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent className="w-[420px] sm:max-w-[420px] p-0 flex flex-col" data-testid="notifications-panel">
        <SheetHeader className="px-5 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2 text-slate-900">
              <Bell size={18} weight="duotone" className="text-[var(--brand,#E31E24)]" />
              {t("notif.title")}
              {unread > 0 && (
                <span className="text-xs bg-[var(--brand,#E31E24)] text-white px-2 py-0.5 rounded-full font-bold">
                  {unread}
                </span>
              )}
            </SheetTitle>
            {unread > 0 && (
              <Button variant="ghost" size="sm" onClick={markAllRead}
                      data-testid="mark-all-read-btn"
                      className="text-xs text-slate-600 hover:text-[var(--brand,#E31E24)]">
                {t("notif.markAll")}
              </Button>
            )}
          </div>
          <SheetDescription className="text-xs text-slate-500">
            {t("notif.desc")}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">
              <Bell size={32} className="mx-auto mb-2 text-slate-300" weight="duotone" />
              {t("notif.empty")}
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((n) => {
                const sev = SEV_META[n.severity] || SEV_META.info;
                const Icon = sev.icon;
                return (
                  <li key={n.id}>
                    <button
                      onClick={() => openItem(n)}
                      data-testid={`notif-item-${n.id}`}
                      className={`w-full text-left px-5 py-3 hover:bg-slate-50 transition-colors flex gap-3 ${
                        !n.read ? "bg-slate-50/60" : ""
                      }`}
                    >
                      <div className={`flex-shrink-0 w-8 h-8 rounded-sm border ${sev.bg} flex items-center justify-center`}>
                        <Icon size={16} weight="duotone" className={sev.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm leading-snug ${!n.read ? "font-bold text-slate-900" : "text-slate-700"}`}>
                            {n.title}
                          </p>
                          {!n.read && (
                            <span className="flex-shrink-0 w-2 h-2 rounded-full bg-[var(--brand,#E31E24)] mt-1.5"
                                  aria-label={t("notif.unread")} />
                          )}
                        </div>
                        <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{n.message}</p>
                        <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wide">
                          {fmtRelative(n.created_at)}
                        </p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
